import {TextareaVariable} from '../types'
import {textareaWithVariables_buildPill} from '../pill/build'
import {textareaWithVariables_normalizePillSpacers} from '../pill/normalize-spacers'
import {textareaWithVariables_isSpacer} from '../pill/is-spacer'


// Live tokenisation at the caret. Called from the React component's
// `onInput` / `onCompositionEnd` after every keystroke. If the user
// just finished typing a known `{TOKEN}` literally before the caret,
// this function replaces that range with a pill element and moves the
// caret right after it.
//
// Pure helper — takes the editor element and the tokens map, mutates
// the DOM in place, returns true if a tokenisation happened (so the
// caller knows to re-emit). The class wrapping it owns the mode check
// (source mode skips tokenisation) and the change emit.
//
// `lastChar` — when defined, must be `}` for tokenisation to run; this
// short-circuits work when the user types a non-closing character.
// Pass `undefined` (e.g. from `compositionend`) to force the full check.
export function textareaWithVariables_tokenizeAtCaret(
    editor   : HTMLElement,
    tokens   : Map<string, TextareaVariable>,
    lastChar : string | undefined,
): boolean {
    if (lastChar !== undefined && lastChar !== '}') return false

    const sel = window.getSelection()
    if (!sel || !sel.isCollapsed || sel.rangeCount === 0) return false

    const range = sel.getRangeAt(0)
    const node  = range.startContainer
    if (node.nodeType !== Node.TEXT_NODE || !editor.contains(node)) return false

    const text   = node.textContent || ''
    const offset = range.startOffset
    if (text[offset - 1] !== '}') return false

    const openIdx = text.lastIndexOf('{', offset - 2)
    if (openIdx === -1) return false

    // Any single-level `{...}` literal (no nested braces) — kept in
    // sync with the matcher in `dom/parse-value.ts`.
    const candidate = text.slice(openIdx, offset)
    if (!/^\{[^{}]+\}$/.test(candidate)) return false

    const def = tokens.get(candidate)
    if (!def) return false

    const before = text.slice(0, openIdx)
    const after  = text.slice(offset)
    const parent = node.parentNode
    if (!parent) return false

    if (before) {
        parent.insertBefore(document.createTextNode(before), node)
    }
    const pill = textareaWithVariables_buildPill(def)
    parent.insertBefore(pill, node)

    // Apply the text split, THEN normalise spacers, THEN place the caret.
    // Order matters: normalise may add a trailing edge-spacer after the
    // pill, and we want the caret to land AFTER that spacer (not wedged
    // between the pill and the spacer, which the snapper would have to fix).
    if (after) {
        node.textContent = after
    } else {
        parent.removeChild(node)
    }

    textareaWithVariables_normalizePillSpacers(editor)

    const newRange = document.createRange()
    if (after) {
        newRange.setStart(node, 0)        // `node` now holds the trailing text
    } else {
        const tail = pill.nextSibling
        newRange.setStartAfter(tail && textareaWithVariables_isSpacer(tail) ? tail : pill)
    }
    newRange.collapse(true)
    sel.removeAllRanges()
    sel.addRange(newRange)
    return true
}
